import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPerformanceReviewCycle, getPerformanceReviewStatus } from '../api/performanceReviewApi';
import toast from 'react-hot-toast';

/** Must match backend `admins` on performance-review cycle routes (list/create/patch). */
const CYCLE_ADMIN_ROLES = new Set(['superadmin', 'admin']);

export function canSetupPerformanceReviewCycle(role) {
  return CYCLE_ADMIN_ROLES.has(String(role || '').toLowerCase());
}

/** POST .../submissions/:id/publish — same roles as backend `admins` on that route. */
export function canPublishPerformanceReview(role) {
  return CYCLE_ADMIN_ROLES.has(String(role || '').toLowerCase());
}

/**
 * Product toggle: hide the "Publish to employee" control for every role (including admin/superadmin).
 * Set to `true` when you want that action visible again. Backend route is unchanged.
 */
export const PERFORMANCE_REVIEW_PUBLISH_TO_EMPLOYEE_UI_ENABLED = false;

/**
 * Inline form: create or upsert a calendar cycle (POST /performance-review/cycles).
 */
export function PerformanceReviewCycleSetupForm({ initialYear, initialQuarter, onSuccess, compact }) {
  const qc = useQueryClient();
  const d = new Date();
  const [year, setYear] = useState(initialYear ?? d.getFullYear());
  const [quarter, setQuarter] = useState(initialQuarter ?? Math.floor(d.getMonth() / 3) + 1);
  const [openEmp, setOpenEmp] = useState(true);
  const [openMgr, setOpenMgr] = useState(true);

  useEffect(() => {
    if (initialYear != null) setYear(initialYear);
    if (initialQuarter != null) setQuarter(initialQuarter);
  }, [initialYear, initialQuarter]);

  const mutation = useMutation({
    mutationFn: () =>
      createPerformanceReviewCycle({
        year: Number(year),
        quarter: Number(quarter),
        is_open_for_employees: openEmp,
        is_open_for_managers: openMgr,
      }),
    onSuccess: () => {
      toast.success('Review period saved');
      qc.invalidateQueries({ queryKey: ['performance-review-status'] });
      qc.invalidateQueries({ queryKey: ['performance-review-me'] });
      onSuccess?.();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Could not save review period'),
  });

  const pad = compact ? '0.65rem' : '0.75rem';
  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: pad,
        background: '#f8fafc',
        marginTop: compact ? 0 : 12,
      }}
    >
      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 10 }}>Create review period</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>
          Year
          <input
            type="number"
            min={2000}
            max={2100}
            value={year}
            onChange={(e) => setYear(e.target.value)}
            style={{
              width: 88,
              padding: '0.35rem 0.45rem',
              borderRadius: 6,
              border: '1px solid #cbd5e1',
              fontSize: '0.85rem',
            }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>
          Quarter
          <select
            value={quarter}
            onChange={(e) => setQuarter(Number(e.target.value))}
            style={{
              padding: '0.35rem 0.45rem',
              borderRadius: 6,
              border: '1px solid #cbd5e1',
              fontSize: '0.85rem',
              minWidth: 72,
            }}
          >
            {[1, 2, 3, 4].map((q) => (
              <option key={q} value={q}>
                Q{q}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#475569', cursor: 'pointer' }}>
          <input type="checkbox" checked={openEmp} onChange={(e) => setOpenEmp(e.target.checked)} />
          Open for employees
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#475569', cursor: 'pointer' }}>
          <input type="checkbox" checked={openMgr} onChange={(e) => setOpenMgr(e.target.checked)} />
          Open for managers
        </label>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          style={{
            padding: '0.45rem 0.9rem',
            borderRadius: 6,
            border: 'none',
            background: '#1272B3',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.8rem',
            cursor: mutation.isPending ? 'wait' : 'pointer',
            opacity: mutation.isPending ? 0.85 : 1,
          }}
        >
          {mutation.isPending ? 'Saving…' : 'Save period'}
        </button>
      </div>
    </div>
  );
}

/** Shown on Team Activity when the feature is on but no cycle row exists yet (admin / superadmin only). */
export function PerformanceReviewCycleMissingBanner() {
  const { data, isLoading } = useQuery({
    queryKey: ['performance-review-status'],
    queryFn: () => getPerformanceReviewStatus(),
    staleTime: 30_000,
  });

  if (isLoading || !data?.enabled || data?.cycle) return null;

  return (
    <div
      style={{
        marginBottom: '1.25rem',
        padding: '1rem 1.1rem',
        borderRadius: 10,
        border: '1px solid #a9ddf5',
        background: 'linear-gradient(135deg, #EAF6FD 0%, #f8fafc 100%)',
        color: '#1e3a5f',
      }}
    >
      <div style={{ fontWeight: 800, fontSize: '0.95rem', marginBottom: 6 }}>Performance review — set up a period</div>
      <p style={{ margin: '0 0 0.75rem', fontSize: '0.88rem', lineHeight: 1.5, color: '#334155' }}>
        The feature is enabled, but no review cycle exists in the database yet. Create the calendar quarter you want to use
        (you can adjust windows with checkboxes).
      </p>
      <PerformanceReviewCycleSetupForm compact />
    </div>
  );
}
